
import { useState, useRef } from 'react';

interface CalendarNewsletterProps {
  formUrl: string;
}

export default function CalendarNewsletter({ formUrl }: CalendarNewsletterProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    setStatus('loading');
    try {
      const body = new URLSearchParams();
      body.append('email', email);

      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="bg-teal-600 rounded-xl p-8 text-center">
      <i className="ri-notification-3-line text-3xl text-white/80 mb-3 block"></i>
      <h3 className="text-xl font-bold text-white mb-2">Never Miss an Event</h3>
      <p className="text-sm text-teal-100 mb-5 max-w-md mx-auto">
        Get weekly event updates delivered to your inbox. Be the first to know about new festivals, concerts, and community gatherings.
      </p>

      {status === 'success' ? (
        <div className="bg-white/20 rounded-lg px-6 py-3 inline-flex items-center space-x-2">
          <i className="ri-check-line text-white text-lg"></i>
          <span className="text-white text-sm font-medium">You&apos;re subscribed! Check your inbox.</span>
        </div>
      ) : (
        <form
          ref={formRef}
          id="calendar-newsletter-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
        >
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            className="flex-1 px-4 py-2.5 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="bg-white text-teal-600 px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
          >
            {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
          </button>
        </form>
      )}

      {status === 'error' && (
        <p className="text-white/80 text-xs mt-3">Something went wrong. Please try again.</p>
      )}
    </section>
  );
}
